import parseISO from "date-fns/parseISO";
import parse from "date-fns/parse";
import formatISO from "date-fns/formatISO";
import isValid from "date-fns/isValid";
import { toZonedTime, formatInTimeZone } from "date-fns-tz";
import { DATE_TIME_TEMPLATE } from "./constants";
import set from "lodash-es/set";

export function getTimeZone(): string {
  const formatter = new Intl.DateTimeFormat();
  const options = formatter.resolvedOptions();
  return options.timeZone;
}

function toUTCDate(date: string): Date | null {
  try {
    const utilDate = parseISO(date);
    if (isValid(utilDate)) {
      const year = utilDate.getFullYear();
      const month = utilDate.getMonth();
      const day = utilDate.getDate();
      const hours = utilDate.getHours();
      const minutes = utilDate.getMinutes();
      const seconds = utilDate.getSeconds();
      return new Date(Date.UTC(year, month, day, hours, minutes, seconds));
    }
    return null;
  } catch {
    return null;
  }
}

export function utcToLocalDate(
  utcString: string,
  timeZone: string = getTimeZone()
): Date | null {
  const utcDate = toUTCDate(utcString);
  if (utcDate) {
    return toZonedTime(utcDate, timeZone);
  }
  return null;
}

export function utcToLocalDateBulk<T>(
  structure: T,
  fields: string[],
  timeZone: string = getTimeZone()
) {
  return fields.reduce(
    (curr, next) => {
      if (structure?.[next]) {
        set(curr as object, next, utcToLocalDate(structure[next], timeZone));
      }
      return curr;
    },
    { ...structure }
  ) as object;
}

export function formatToUTC(
  localDateTime: Date,
  template: string
): string | null {
  try {
    return formatInTimeZone(localDateTime, "UTC", template);
  } catch {
    return null;
  }
}

export function formatStringToISO8601(localDateTime: string, template: string) {
  const parsed = parse(localDateTime, template, new Date());
  const iso = formatISO(parsed);

  return iso;
}

export function formatStringToUTC(
  localDateTime: string,
  template: string
): string | null {
  try {
    const iso = formatStringToISO8601(localDateTime, template);
    return formatInTimeZone(iso, "UTC", template);
  } catch (e) {
    if (e instanceof RangeError) {
      console.error("formatStringToUTC needs a template with time and seconds");
    }
    console.error(e);
    return null;
  }
}

export function formatToUTCBulk<T>(
  structure: T,
  fields: string[],
  template: string = DATE_TIME_TEMPLATE
) {
  return fields.reduce((curr, next) => {
    if (structure?.[next]) {
      set(curr as object, next, formatToUTC(structure[next], template));
    }
    return curr;
  }, structure) as object;
}
